import Icon from '@mui/icons-material/Error'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { TFunction } from 'i18next'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import usePutConsentByID from '../../api/v1/accounts/usePutConsentByID'
import Countdown from '../../components/Countdown'
import { BodyPaper, Header } from '../../components/Layout'
import { ChildLandingPage } from '../../pages'
import { AlertData, AlertType, useAlert } from '../../providers/alert'
import { useAuth } from '../../providers/auth'

/**
 * `PendingPageError` enumerates possible error states for component `PendingPage`.
 */
enum PendingPageError {
  /**
   * `MissingData` means data required to show `PendingPage` is missing or invalid.
   */
  MissingData = 'missingData',

  /**
   * `ResendFailed` means the attempt to resend the parental consent request failed.
   */
  ResendFailed = 'resendFailed',
}

/**
 * `makeErrorAlert` creates an error alert message for `PendingPage`.
 *
 * TODO adapt this for reuse by other components and export it from AlertProvider
 * @param t - The translation function from the `useTranslation` hook.
 * @param error - The error state of the `PendingPage` component.
 * @return {AlertData} - The error alert message object.
 */
const makeErrorAlert = (t: TFunction<string, string>, error: PendingPageError): AlertData => ({
  severity: AlertType.ERROR,
  msg: `${t('statusMessage.errorLabel')}: ${t(`statusMessage.error.${error}.brief`)}`,
  duration: 5000,
})

/**
 * `makeNewRequestAllowedAt` returns the datetime after which the user can make a
 * new verifiable parental consent request.
 *
 * The datetime's seconds are set to 0 because, since the countdown widget
 * displays only hours and minutes, it is a better user experience to have the
 * countdown end exactly when the minutes go from 1 to 0.
 * @param originalRequestDateTime {string}
 * @param numHoursUntilNextRequest {number}
 * @return {Date} - The datetime when a new request is allowed, with seconds set to 0.
 */
const makeNewRequestAllowedAt = (
  originalRequestDateTime: string,
  numHoursUntilNextRequest: number,
): Date => {
  const consentRequestedAt = new Date(originalRequestDateTime)
  const newRequestAllowedAt = new Date(
    consentRequestedAt.getTime() + numHoursUntilNextRequest * 60 * 60 * 1000,
  )
  newRequestAllowedAt.setSeconds(0, 0)
  return newRequestAllowedAt
}

interface PendingPageProps {
  /**
   * `numHoursUntilNewRequestAllowed` is the number of hours until the user can
   * make a new verifiable parental consent request. The countdown widget
   * displays the number of hours and minutes remaining.
   *
   * - Set a value greater than 0 to enable the countdown widget.
   * - If you set a value less than or equal to 0, the countdown widget will not display.
   * - Avoid incredibly large values (e.g., 10,000) since the countdown widget is not
   *   designed to display them and may display unexpected values if overflows occur.
   * @type {number}
   * @defaultValue 48
   */
  numHoursUntilNewRequestAllowed?: number
}

/**
 * `PendingPage` is a React functional component that allows authenticated users
 * to view and edit their pending verifiable parental consent (VPC) request.
 *
 * The component displays the status of the user's VPC request, including the
 * date and time of the request, the parent/guardian's email address, and a
 * countdown until the user can make a new request.
 *
 * The component also provides buttons to resend the request and edit the
 * parent/guardian's email address. These buttons are disabled until the
 * countdown ends.
 *
 * If the user is not authenticated, they are redirected to the login page.
 *
 * If the user data is missing or invalid, an error message is displayed.
 * @param {PendingPageProps} props
 */
const PendingPage: FC<PendingPageProps> = (props: PendingPageProps) => {
  const [editing, setEditing] = useState(false)
  return editing ? (
    <ChildLandingPage handleCancel={() => setEditing(false)} />
  ) : (
    <View {...props} onEditFormClick={() => setEditing(true)} />
  )
}

interface ViewProps extends PendingPageProps {
  /**
   * `onEditFormClick` is a callback function called when the user clicks the
   * "Edit form" button. Nothing happens if this prop is not provided.
   * @type {() => void}
   * @defaultValue undefined
   */
  onEditFormClick?: () => void
}

/**
 * `View` is a React functional component that displays the data for a
 * player's pending verifiable parental consent (VPC) request.
 * @param {ViewProps} props
 */
const View: FC<ViewProps> = ({
  numHoursUntilNewRequestAllowed = 48,
  onEditFormClick = () => {},
}: ViewProps) => {
  const { authState, refreshAuthState } = useAuth()
  const { t } = useTranslation('vpc', { keyPrefix: 'pendingPage' })
  const { showAlert } = useAlert()
  const handledErrorsRef = useRef<Set<string>>(new Set())
  const theme = useTheme()

  const requestDateTime = authState.accountInfo?.consent.requestDateTime || ''
  const parentEmail = authState.accountInfo?.consent.parentEmail

  // lazy load init state of newRequestAllowedAt to avoid incorrect button animation
  const [newRequestAllowedAt, setNewRequestAllowedAt] = useState<Date | null>(() =>
    makeNewRequestAllowedAt(requestDateTime, numHoursUntilNewRequestAllowed),
  )

  const [countingDown, setCountingDown] = useState(() => {
    if (!newRequestAllowedAt) return false // no countdown, enable buttons
    return newRequestAllowedAt.getTime() > Date.now() // counting down, disable buttons
  })

  const [buttonsCoolingDown, setButtonsCoolingDown] = useState(false)

  const {
    data: resendResponse,
    loading: resendInFlight,
    error: resendError,
    callFetch,
  } = usePutConsentByID(
    authState?.accountInfo?.id || '',
    authState.accountInfo?.givenName || '',
    parentEmail || '',
    authState?.jwtRaw || '',
  )

  // update newRequestAllowedAt whenever its component values change
  useEffect(() => {
    setNewRequestAllowedAt(makeNewRequestAllowedAt(requestDateTime, numHoursUntilNewRequestAllowed))
  }, [requestDateTime, numHoursUntilNewRequestAllowed])

  // disable/enable buttons whenever countdown starts/ends
  useEffect(() => {
    if (!newRequestAllowedAt) return
    setCountingDown(newRequestAllowedAt.getTime() > Date.now())
  }, [newRequestAllowedAt])

  // disable buttons while resend request is in flight and for 1 second after
  useEffect(() => {
    if (!resendInFlight && buttonsCoolingDown) {
      const timeoutID = setTimeout(() => {
        setButtonsCoolingDown(false)
      }, 1000)
      return () => clearTimeout(timeoutID)
    } else if (resendInFlight && !buttonsCoolingDown) setButtonsCoolingDown(true)
  }, [resendInFlight, buttonsCoolingDown])

  // display error alert to user if missing data; log warning if request date time
  // is in the future
  useEffect(() => {
    const error = PendingPageError.MissingData
    if (!parentEmail || !requestDateTime || isNaN(new Date(requestDateTime).getTime())) {
      if (handledErrorsRef.current.has(error)) {
        return
      }
      handledErrorsRef.current.add(error)
      showAlert(makeErrorAlert(t, error))
      console.error(
        error,
        `-- parentEmail: "${parentEmail}", requestDateTime: "${requestDateTime}"`,
      )
    } else if (handledErrorsRef.current.has(error)) {
      handledErrorsRef.current.delete(error)
    }

    if (new Date(requestDateTime).getTime() > Date.now()) {
      console.warn(`request date time is in the future: "${requestDateTime}"`)
    }
  }, [parentEmail, requestDateTime, t])

  // on failed resend request, show an error alert
  useEffect(() => {
    if (resendError) {
      showAlert(makeErrorAlert(t, PendingPageError.ResendFailed))
    }
  }, [resendError, t])

  // on successful request, refresh auth state to update this page's data
  useEffect(() => {
    if (resendResponse && !resendInFlight && !resendError) {
      showAlert({
        severity: AlertType.INFO,
        msg: t('statusMessage.resendSuccess', { email: parentEmail }),
        duration: 4000,
      })
      refreshAuthState()
    }
  }, [resendResponse, resendInFlight, resendError, refreshAuthState])

  // `onResendClick` sends a resend parental consent request and shows an alert
  // when the user clicks the "Resend" button.
  const onResendClick = useCallback(() => {
    showAlert({
      severity: AlertType.INFO,
      msg: t('statusMessage.resending', { email: parentEmail }),
      duration: 4000,
    })
    callFetch()
  }, [parentEmail, callFetch])

  const missingData = handledErrorsRef.current.has(PendingPageError.MissingData)
  const renderCountdown = numHoursUntilNewRequestAllowed > 0 && newRequestAllowedAt
  return (
    <Box sx={{ minHeight: '70vh', display: 'flex', placeContent: 'center', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header showPlatformDisplayName title={t('title')} subtitle={t('subtitle')} />
        <BodyPaper
          data-testid="pending-page"
          showExpiration={true}
          showLogoutButton={true}
          sx={{ p: 5, width: '100%' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                pt: `2rem`,
                pb: '1rem',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Icon
                  htmlColor={theme.palette.warning.light}
                  sx={{ width: '4rem', height: '4rem', opacity: 0.8 }}
                />
              </Box>
              <Typography variant="bodyExtraLarge" sx={{ pt: '0rem' }}>
                {missingData ? (
                  t(`statusMessage.error.${PendingPageError.MissingData}.detailed`)
                ) : (
                  <Trans
                    t={t}
                    i18nKey={'statusMessage.pending'}
                    values={{
                      consentRequestDateTime: new Date(requestDateTime),
                      parentEmail: authState.accountInfo?.consent.parentEmail,
                    }}
                  />
                )}
              </Typography>
            </Box>
            {!missingData && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}
              >
                {renderCountdown && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2.5,
                      pt: 10,
                      [theme.breakpoints.down('sm')]: {
                        pt: '2rem',
                      },
                    }}
                  >
                    <Countdown
                      until={newRequestAllowedAt}
                      onCountdownEnd={() => setCountingDown(false)}
                      hoursLabel={t('countdown.hoursLabel')}
                      minutesLabel={t('countdown.minutesLabel')}
                    />
                    <Typography sx={{ pt: '1rem' }}>
                      {t('countdownLabel', {
                        numHoursUntilNewRequestAllowed,
                        count: numHoursUntilNewRequestAllowed,
                      })}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2.5,
                    pt: 1,
                  }}
                  title={
                    countingDown
                      ? t('disabledButtonsExplanation') +
                        ' ' +
                        t('countdownLabel', {
                          numHoursUntilNewRequestAllowed,
                          count: numHoursUntilNewRequestAllowed,
                        })
                      : undefined
                  }
                >
                  <Button
                    data-testid="pending-resend-button"
                    variant="contained"
                    disabled={countingDown || buttonsCoolingDown}
                    onClick={onResendClick}
                    sx={{ minWidth: '13rem' }}
                  >
                    {resendInFlight ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: '0.5rem' }} />
                        <Typography variant="buttonMedium">{t('resendingButton')}</Typography>
                      </>
                    ) : (
                      <Typography variant="buttonMedium">{t('resendButton')}</Typography>
                    )}
                  </Button>
                  <Button
                    data-testid="pending-edit-button"
                    variant="outlined"
                    disabled={countingDown || buttonsCoolingDown}
                    onClick={onEditFormClick}
                    sx={{ minWidth: '13rem' }}
                  >
                    <Typography variant="buttonMedium">{t('editButton')}</Typography>
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </BodyPaper>
      </Box>
    </Box>
  )
}

export default PendingPage
